// Phase C — frontend host context.
// In the browser, window.location.hostname IS the real public host (custom domain or
// <label>.solunary.health). We classify the host to decide whether to render a tenant
// public site (and let the backend resolve the tenant by Host) or the platform app.

export const PLATFORM_DOMAIN = (process.env.REACT_APP_PLATFORM_DOMAIN || "solunary.health").toLowerCase();

const RESERVED = new Set([
  "www", "app", "api", "admin", "portal", "mail", "static", "cdn",
  "assets", "blog", "care", "go", "dashboard", "status", "docs", "origin", "domains",
]);

export function getHostContext() {
  if (typeof window === "undefined") return { mode: "platform", host: "" };
  const host = (window.location.hostname || "").toLowerCase();

  if (
    host === PLATFORM_DOMAIN ||
    host === `www.${PLATFORM_DOMAIN}` ||
    host.endsWith(".emergentagent.com") ||
    host.endsWith(".emergentcf.cloud") ||
    host === "localhost" ||
    host === "127.0.0.1"
  ) {
    return { mode: "platform", host };
  }

  if (host.endsWith(`.${PLATFORM_DOMAIN}`)) {
    const label = host.slice(0, -(PLATFORM_DOMAIN.length + 1));
    if (label && !label.includes(".") && !RESERVED.has(label)) {
      return { mode: "tenant", kind: "subdomain", subdomain: label, host };
    }
    return { mode: "platform", host };
  }

  // Anything else = a customer custom domain pointed at us → tenant public site.
  return { mode: "tenant", kind: "custom", host };
}

export function isTenantHost() {
  return getHostContext().mode === "tenant";
}

// Adresse publique RÉELLE du site d'un tenant, adaptée à l'environnement courant :
//   - prod  (PLATFORM_DOMAIN=solunary.health, https, sans port) → https://<sub>.solunary.health
//   - dev   (PLATFORM_DOMAIN=localhost, port 3000)              → http://<sub>.localhost:3000
//     (les navigateurs résolvent nativement *.localhost → 127.0.0.1)
// `loc` est paramétrable pour les tests ; par défaut window.location.
export function publicSiteUrl(subdomain, loc = window.location) {
  if (!subdomain) return "";
  const port = loc.port ? `:${loc.port}` : "";
  return `${loc.protocol}//${subdomain}.${PLATFORM_DOMAIN}${port}`;
}

// Origine de la PLATEFORME vue depuis n'importe quel hôte tenant — c'est la seule
// origine enregistrée auprès de Google (GIS), donc la seule où le sign-in peut
// s'exécuter. dev: http://localhost:3000 ; prod: https://solunary.health.
export function platformOrigin(loc = window.location) {
  const port = loc.port ? `:${loc.port}` : "";
  return `${loc.protocol}//${PLATFORM_DOMAIN}${port}`;
}

// The tenant's REAL public site URL, derived from its SAVED fields — the frontend
// counterpart of the backend's `_tenant_public_origin` (modules/payments/service.py).
// The custom-domain and subdomain rules match it exactly, so an admin-UI link and an
// email link agree for every ordinary tenant:
//   1. LIVE custom domain → https://{domain}. `custom_domain_status === "live"` is the
//      gate (backend modules/custom_domains: certificate issued, domain active on the
//      edge): any other status — DNS pending, certificate pending, failed, lapsed — is an
//      address that does not answer HTTPS, and linking it is a dead "View site" button.
//   2. root/demo tenant → {platformOrigin}/demo — the one site the platform host
//      serves directly (same rule as useTenantHomePath, BUG-039). This branch is
//      frontend-ONLY, a deliberate divergence: the backend's value is an ORIGIN that
//      callers append paths to ("/merci/boutique"), and "/demo" is origin+path — so
//      server-built links for the root tenant use its subdomain origin instead, which
//      the host resolver serves equally. In-app links prefer /demo because that is the
//      address the platform advertises for the demo site.
//   3. platform subdomain → publicSiteUrl(subdomain || slug). `subdomain` first: a
//      rename moves `subdomain` and leaves `slug` behind (former_subdomains redirect).
// Returns "" when nothing can be derived, so callers keep their own fallback.
export function tenantSiteUrl(tenant, loc = window.location) {
  if (!tenant) return "";
  const cd = (tenant.custom_domain || "").trim();
  if (cd && tenant.custom_domain_status === "live") {
    return `https://${cd.replace(/^https?:\/\//i, "").toLowerCase()}`;
  }
  if (tenant.is_root) return `${platformOrigin(loc)}/demo`;
  return publicSiteUrl(tenant.subdomain || tenant.slug, loc);
}
